// Per-field validity, reported up to the dialog that owns the Save button.
//
// A field that refuses to write a half-finished value into form state has only done
// half the job: without this, saving while a date box reads "12/05/" would quietly
// store the PREVIOUS date. So fields register themselves as unfinished here, and
// <Modal> disables Save while any of them is.
//
// Registration is per field and carries the field's LABEL, not a global boolean —
// with two date fields on one form (a retainer's start and end), the dialog has to
// say which one is waiting, or the block is just a dead button.
import { createContext, useCallback, useState } from 'react';

// null when there is no surrounding Modal. Consumers must treat that as "no gating
// available" and carry on, never as an error — a control can legitimately live
// outside a dialog.
export const FieldValidityContext = createContext(null);

// Owned by Modal. `report(id, label)` marks a field unfinished; `report(id, null)`
// clears it, and every field clears on unmount so a closed section cannot leave a
// dialog permanently unsaveable.
export function useFieldRegistry() {
  const [fields, setFields] = useState({});

  const report = useCallback((id, label) => {
    setFields((prev) => {
      if (label == null) {
        if (!(id in prev)) return prev; // no state change ⇒ no re-render loop
        const next = { ...prev };
        delete next[id];
        return next;
      }
      if (prev[id] === label) return prev;
      return { ...prev, [id]: label };
    });
  }, []);

  return { unfinished: Object.values(fields), report };
}
